/**
 * IST session helpers — typed core for Bharat Charts.
 */
import type { BcBar } from "./globals";

export const IST_OFFSET_MS = 5.5 * 60 * 60 * 1000;
export const SESSION_OPEN_MIN = 9 * 60 + 15;
export const SESSION_CLOSE_MIN = 15 * 60 + 30;
/** F&O cash stocks: continuous trading ends 15:15 (closing auction 15:15–15:30, no 5m bars). */
export const SESSION_FNO_CLOSE_MIN = 15 * 60 + 15;
export const MCX_OPEN_MIN = 9 * 60;
export const MCX_CLOSE_MIN = 23 * 60 + 30;
/** GIFT Nifty (NSE IX) — near round-the-clock weekdays; use 06:30–23:55 same-day for bar bucketing. */
export const GIFT_OPEN_MIN = 6 * 60 + 30;
export const GIFT_CLOSE_MIN = 23 * 60 + 55;

/** Uppercase labels that use the F&O closing auction (no continuous bars after 15:15). */
let fnoAuctionSyms: Set<string> | null = null;

/** Call after watchlist load so hole detection / session close match exchange hours. */
export function setFnoClosingAuctionSymbols(labels: Array<string | null | undefined> | null | undefined): void {
  const next = new Set<string>();
  (labels || []).forEach(function (lab) {
    const u = String(lab || "")
      .trim()
      .toUpperCase();
    if (u) next.add(u);
  });
  fnoAuctionSyms = next.size ? next : null;
}

export function hasFnoClosingAuction(symbol: string | null | undefined): boolean {
  if (!fnoAuctionSyms || !fnoAuctionSyms.size) return false;
  return fnoAuctionSyms.has(String(symbol || "").trim().toUpperCase());
}

export function isMcxSymbol(s: string | null | undefined): boolean {
  // Universal MCX session hours — not crude-only.
  const v = String(s || "");
  return (
    /CRUDE|MCX|GOLD|SILVER|NATGAS|NATURAL\s*GAS|COPPER|ZINC|LEAD|NICKEL|ALUMINIUM|MENTHA/i.test(v) ||
    /^(ALUMINI|COTTON|COTTONOIL|CARDAMOM|KAPAS|STEELREBAR|ELECDMBL)\s+[A-Z]{3}\s+FUT$/i.test(v)
  );
}

export function isGiftSymbol(s: string | null | undefined): boolean {
  return /GIFT\s*NIFTY/i.test(String(s || ""));
}

export function sessionOpenMin(symbol: string): number {
  if (isGiftSymbol(symbol)) return GIFT_OPEN_MIN;
  return isMcxSymbol(symbol) ? MCX_OPEN_MIN : SESSION_OPEN_MIN;
}

export function sessionCloseMin(symbol: string): number {
  if (isGiftSymbol(symbol)) return GIFT_CLOSE_MIN;
  if (isMcxSymbol(symbol)) return MCX_CLOSE_MIN;
  // F&O equities: continuous session ends 15:15 — 15:15→15:30 is auction, not a candle hole.
  if (hasFnoClosingAuction(symbol)) return SESSION_FNO_CLOSE_MIN;
  return SESSION_CLOSE_MIN;
}

export function resMinutes(res: string | number | null | undefined): number {
  const r = String(res || "5").toUpperCase();
  if (r === "1D" || r === "D") return 1440;
  if (r === "4H" || r === "240") return 240;
  if (r === "1H" || r === "60") return 60;
  const n = parseInt(r, 10);
  return n > 0 ? n : 5;
}

export function istDayStartMs(ms: number): number {
  const shifted = new Date(Number(ms) + IST_OFFSET_MS);
  return (
    Date.UTC(shifted.getUTCFullYear(), shifted.getUTCMonth(), shifted.getUTCDate(), 0, 0, 0) -
    IST_OFFSET_MS
  );
}

export function floorBarTimeMs(nowMs: number, resolution: string, symbol: string): number {
  const stepMin = resMinutes(resolution);
  const shifted = new Date(nowMs + IST_OFFSET_MS);
  if (stepMin >= 1440) {
    return (
      Date.UTC(shifted.getUTCFullYear(), shifted.getUTCMonth(), shifted.getUTCDate(), 0, 0, 0) -
      IST_OFFSET_MS
    );
  }
  const openMin = sessionOpenMin(symbol);
  const closeMin = sessionCloseMin(symbol);
  let minutes = shifted.getUTCHours() * 60 + shifted.getUTCMinutes();
  if (minutes < openMin) minutes = openMin;
  if (minutes > closeMin) minutes = closeMin;
  const offset = minutes - openMin;
  const aligned = openMin + Math.floor(offset / stepMin) * stepMin;
  const h = Math.floor(aligned / 60);
  const m = aligned % 60;
  return (
    Date.UTC(shifted.getUTCFullYear(), shifted.getUTCMonth(), shifted.getUTCDate(), h, m, 0) -
    IST_OFFSET_MS
  );
}

/** Before today's session open (NSE pre-open 09:00–09:15, MCX before 09:00): quote only, no candle. */
export function isPreOpenAt(symbol: string, ms: number): boolean {
  const shifted = new Date(Number(ms) + IST_OFFSET_MS);
  return shifted.getUTCHours() * 60 + shifted.getUTCMinutes() < sessionOpenMin(symbol);
}

export function inSessionAt(symbol: string, ms: number): boolean {
  const shifted = new Date(Number(ms) + IST_OFFSET_MS);
  const dow = shifted.getUTCDay();
  if (dow === 0 || dow === 6) return false;
  const minutes = shifted.getUTCHours() * 60 + shifted.getUTCMinutes();
  return minutes >= sessionOpenMin(symbol) && minutes <= sessionCloseMin(symbol);
}

export function inSessionNow(symbol: string): boolean {
  return inSessionAt(symbol, Date.now());
}

export function stripOpenBucket(
  bars: BcBar[] | null | undefined,
  resolution: string,
  symbol: string
): BcBar[] {
  const list = bars || [];
  if (!list.length) return list;
  const bucket = floorBarTimeMs(Date.now(), resolution, symbol);
  if (list[list.length - 1].time >= bucket) return list.slice(0, -1);
  return list;
}
